import { AccordionDetails, Box } from '@mui/material';
import { FC, ReactNode } from 'react';

import { AbgTypography, CrossIcon, formatMoney, mergeSx } from '@abg/kit-base';
import { AbgSxProps } from '@abg/typings';

import { useGlobalRegions } from '../../../../hooks';
import { GlobalRegion } from '../../../../shared/constants';
import { useTranslationContext } from '../../../../shared/context';
import { AncillariesI18nKeys } from '../../../../shared/types';
import { CostListType } from '../../car-info-accordion.types';
import {
  carInfoAccordionStyling,
  checkoutMobileAccordionDetailsStyling,
} from '../../CarInfoAccordion.styling';

import {
  carInfoDetailsStyling,
  checkoutMobileCarInfoWrapper,
  checkoutMobileHeaderText,
  checkoutMobileRecentCost,
  deleteIconContainerStyling,
  deleteIconStyling,
  gsoEstimatedText,
} from './CarInfoDetails.styling';
export const CarInfoDetails: FC<CarInfoDetailsProps> = ({
  costList,
  stylePart,
  children,
  currency = 'USD',
  isCheckoutMobile,
  isAvisFirst,
  gsoAvisFirstTitle,
  afeTranslationsTitle,
  loading,
  onCostRemove,
}) => {
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const { belongsToRegions } = useGlobalRegions();
  const isEmea = belongsToRegions([GlobalRegion.EMEA]);

  const handleDeleteItem = (item: CostListType) => {
    if (onCostRemove && item.code) {
      onCostRemove(item.code);
    }
  };

  const renderDetails = () => {
    const hasRemovableItems =
      costList?.some((cost) => cost.removable) && !!onCostRemove;

    return costList?.map((cost, index) => {
      if (cost.description === 'AARP Credit' && cost.amount === 0) {
        //ARGWK-336 Should display 'AARP Credit' only for AARP user
        return null;
      }
      if (gsoAvisFirstTitle && (cost.code === 'GSO' || cost.code === 'AFHFF')) {
        // Apply GSO/AFHFF translation for Avis First scenario: ARGWE-13676
        cost.description = gsoAvisFirstTitle;
      } else if (afeTranslationsTitle && cost.code === 'AFE') {
        // Apply AFE translation for Avis First scenario: ARGWE-15336
        cost.description = afeTranslationsTitle;
      }

      const amountText = getAmountText({
        cost,
        isEmea,
        i18nKeys,
        currency,
        t,
      });

      return (
        <Box
          key={cost.description}
          sx={
            {
              ...mergeSx(carInfoDetailsStyling.wrapper, stylePart),
              ...(isCheckoutMobile ? checkoutMobileCarInfoWrapper : {}),
            } as AbgSxProps
          }
        >
          <AbgTypography
            component="span"
            variant={'bodySmallRegular'}
            sx={
              isCheckoutMobile
                ? checkoutMobileHeaderText
                : carInfoDetailsStyling.headerText
            }
            data-testid={`cost-${index + 1}-description`}
          >
            {cost.code !== 'GSO' && cost.code !== 'AFHFF' && cost.description}
            {(cost.code === 'GSO' || cost.code === 'AFHFF') && (
              <>
                {isAvisFirst
                  ? t(i18nKeys.gsoFuelHassleFree)
                  : t(i18nKeys.gsoFuelUpfront)}

                {!isEmea && (
                  <AbgTypography
                    component="div"
                    variant="bodySmallRegular"
                    sx={gsoEstimatedText}
                  >
                    {t(i18nKeys.estimatedLabel)} {currency}{' '}
                    {(cost.netSubtotalPerUnit ?? 0).toFixed(2)}/
                    {t(i18nKeys.itemPriceLabel)}
                  </AbgTypography>
                )}
              </>
            )}
          </AbgTypography>

          <Box sx={carInfoDetailsStyling.costsContainer}>
            <AbgTypography
              component="span"
              variant={'bodySmallRegular'}
              sx={
                isCheckoutMobile
                  ? checkoutMobileRecentCost
                  : carInfoDetailsStyling.recentCost
              }
              data-testid={`cost-${index + 1}-amount`}
            >
              {amountText}
            </AbgTypography>

            {hasRemovableItems && isEmea && (
              <Box sx={deleteIconContainerStyling}>
                {cost.removable && (
                  <CrossIcon
                    sx={deleteIconStyling}
                    onClick={() => !loading && handleDeleteItem(cost)}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      );
    });
  };

  return (
    <AccordionDetails
      sx={
        isCheckoutMobile
          ? checkoutMobileAccordionDetailsStyling
          : carInfoAccordionStyling.accordionDetails
      }
    >
      {children || renderDetails() || 'No data provided'}
    </AccordionDetails>
  );
};

const getAmountText = ({
  cost,
  isEmea,
  i18nKeys,
  currency,
  t,
}: {
  cost: CostListType;
  isEmea: boolean;
  i18nKeys: AncillariesI18nKeys;
  currency?: string;
  t: (key: string) => string;
}): string => {
  // Any item flagged as priceOnRequest in EMEA — show TBC
  if (isEmea && cost.priceOnRequest) {
    return t(i18nKeys.tbcLabel) || 'TBC';
  }

  // EMEA upgrade flow GSO shows TBC
  if (isEmea && cost.code === 'GSO') {
    return t(i18nKeys.tbcLabel) || 'TBC';
  }

  // AFHFF shows market-price
  if (isEmea && cost.code === 'AFHFF') {
    return t(i18nKeys.marketPriceLabel);
  }

  // NAM/APAC: GSO with zero amount shows "Market Price"
  if (!isEmea && cost.code === 'GSO' && cost.amount === 0) {
    return t(i18nKeys.marketPriceLabel);
  }

  if (cost.code === 'FUE' && cost.amount === 0) {
    return t(i18nKeys.tbcLabel);
  }

  return formatMoney(cost.amount ?? 0, { currency: currency ?? '' });
};

export type CarInfoDetailsProps = {
  costList?: CostListType[] | undefined;
  onCostRemove?: ((itemId: string) => void) | undefined;
  children?: ReactNode;
  stylePart?: AbgSxProps | undefined;
  currency?: string | undefined;
  isCheckoutMobile?: boolean | undefined;
  isAvisFirst?: boolean | undefined;
  gsoAvisFirstTitle?: string | undefined;
  afeTranslationsTitle?: string | undefined;
  loading?: boolean | undefined;
};
